import * as moment from 'moment';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import * as React from 'react';
import * as images from '../../images/images';
import { CONSTANTS } from '../../styles/styleConstants';
import * as style from '../../styles/styleObjects';
import { PlaybackState } from './AudioPlayer';
import { RecordingState } from './MediaRecorder';

const LABEL_FORMAT = 'mm:ss';

export enum AudioRecorderUseMode {
    Recording,
    Reviewing
}

export type AudioButton = React.ComponentType<{
    style?: AudioViewStyleProp;
    onClick?(): void;
    playbackState: PlaybackState;
    recordingState: RecordingState;
    mode: AudioRecorderUseMode;
}>;
export type ProgressBar = React.ComponentType<{
    style?: AudioViewStyleProp;
    onChange?(value: number): void;
    current: number;
    max: number;
    disabled?: boolean;
}>;

export type AudioViewStyleProp =
    | undefined
    | React.CSSProperties
    | ((mode: AudioRecorderUseMode, recordingState: RecordingState, playbackState: PlaybackState) => React.CSSProperties);

export interface IAudioViewProps {
    useMode: AudioRecorderUseMode;
    currentTime: number;
    maxTime: number;
    onClickStart(): void;
    onClickPause(): void;
    onClickStop(): void;
    onClickConfirm?(): void;
    onClickCancel?(): void;
    onProgressChange?(value: number): void;
    onProgressComplete?(value: number): void;
    progressDisabled?: boolean;
    style?: React.CSSProperties;
    buttonContainerStyle?: AudioViewStyleProp;
    startButtonStyle?: AudioViewStyleProp;
    pauseButtonStyle?: AudioViewStyleProp;
    stopButtonStyle?: AudioViewStyleProp;
    confirmButtonStyle?: AudioViewStyleProp;
    cancelButtonStyle?: AudioViewStyleProp;
    progressStyle?: AudioViewStyleProp;
    progressLabelStyle?: AudioViewStyleProp;
    progressBarStyle?: AudioViewStyleProp;
    progressTrackStyle?: AudioViewStyleProp;
    progressBackgroundStyle?: AudioViewStyleProp;
    progressHandleStyle?: AudioViewStyleProp;
    StartButtonComponent?: AudioButton;
    PauseButtonComponent?: AudioButton;
    StopButtonComponent?: AudioButton;
    ConfirmButtonComponent?: AudioButton;
    CancelButtonComponent?: AudioButton;
    ProgressBarComponent?: ProgressBar;
    placeholderText?: string;
    placeholderTextStyle?: AudioViewStyleProp;

    recordingState: RecordingState;
    playbackState: PlaybackState;
}

export abstract class AbstractAudioView<P> extends React.Component<P & IAudioViewProps> {
    protected renderStartButton() {
        const StartButtonComponent = this.props.StartButtonComponent as AudioButton | undefined;

        if (StartButtonComponent) {
            return (
                <StartButtonComponent
                    style={{ ...style.recordingButtonStyle, ...this.getStyleObjectFromProp(this.props.startButtonStyle) }}
                    onClick={this.props.onClickStart}
                    recordingState={this.props.recordingState}
                    playbackState={this.props.playbackState}
                    mode={this.props.useMode}
                />
            );
        }

        return (
            <div onClick={this.props.onClickStart} style={{ ...style.recordingButtonStyle, ...this.getStyleObjectFromProp(this.props.startButtonStyle) }}>
                <img src={images.PlayButton} />
            </div>
        );
    }

    protected renderPauseButton() {
        const PauseButtonComponent = this.props.PauseButtonComponent as AudioButton | undefined;
        if (PauseButtonComponent) {
            return (
                <PauseButtonComponent
                    style={{ ...style.recordingButtonStyle, ...this.getStyleObjectFromProp(this.props.pauseButtonStyle) }}
                    onClick={this.props.onClickPause}
                    recordingState={this.props.recordingState}
                    playbackState={this.props.playbackState}
                    mode={this.props.useMode}
                />
            );
        }
        return (
            <div onClick={this.props.onClickPause} style={{ ...style.recordingButtonStyle, ...this.getStyleObjectFromProp(this.props.pauseButtonStyle) }}>
                <img src={images.PauseButtonIcon} />
            </div>
        );
    }

    protected renderStopButton() {
        const StopButtonComponent = this.props.StopButtonComponent as AudioButton | undefined;
        if (StopButtonComponent) {
            return (
                <StopButtonComponent
                    style={{ ...style.recordingButtonStyle, ...this.getStyleObjectFromProp(this.props.stopButtonStyle) }}
                    onClick={this.props.onClickStop}
                    recordingState={this.props.recordingState}
                    playbackState={this.props.playbackState}
                    mode={this.props.useMode}
                />
            );
        }
        return (
            <div onClick={this.props.onClickStop} style={{ ...style.recordingButtonStyle, ...this.getStyleObjectFromProp(this.props.stopButtonStyle) }}>
                <img src={images.StopButton} />
            </div>
        );
    }

    protected renderConfirmButton() {
        const ConfirmButtonComponent = this.props.ConfirmButtonComponent as AudioButton | undefined;
        if (ConfirmButtonComponent) {
            return (
                <ConfirmButtonComponent
                    style={this.props.confirmButtonStyle}
                    onClick={this.props.onClickConfirm}
                    recordingState={this.props.recordingState}
                    playbackState={this.props.playbackState}
                    mode={this.props.useMode}
                />
            );
        }
        return (
            <div onClick={this.props.onClickConfirm} style={this.getStyleObjectFromProp(this.props.confirmButtonStyle) || styles.defaultConfirmButton}>
                <p style={styles.submitStyle}>Submit</p>
            </div>
        );
    }

    protected renderCancelButton() {
        const CancelButtonComponent = this.props.CancelButtonComponent as AudioButton | undefined;
        if (CancelButtonComponent) {
            return (
                <CancelButtonComponent
                    style={this.props.cancelButtonStyle}
                    onClick={this.props.onClickCancel}
                    recordingState={this.props.recordingState}
                    playbackState={this.props.playbackState}
                    mode={this.props.useMode}
                />
            );
        }
        return (
            <div onClick={this.props.onClickCancel} style={this.getStyleObjectFromProp(this.props.cancelButtonStyle) || styles.defaultCancelButton}>
                <p style={{ cursor: 'pointer' }}>Cancel</p>
            </div>
        );
    }

    protected renderProgress() {
        if (
            this.props.placeholderText &&
            this.props.useMode === AudioRecorderUseMode.Recording &&
            this.props.recordingState !== RecordingState.Recording &&
            this.props.currentTime === 0
        ) {
            return <h4 style={this.getStyleObjectFromProp(this.props.placeholderTextStyle)}>{this.props.placeholderText}</h4>;
        }
        const ProgressBarComponent = this.props.ProgressBarComponent as ProgressBar | undefined;
        if (ProgressBarComponent) {
            return (
                <ProgressBarComponent
                    current={this.props.currentTime}
                    max={this.props.maxTime}
                    style={this.props.progressBarStyle}
                    onChange={this.props.onProgressChange}
                />
            );
        }
        const current = moment(this.props.currentTime);
        return (
            <div style={styles.sliderContainer}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <Slider
                        min={0}
                        max={this.props.maxTime}
                        value={this.props.currentTime}
                        disabled={this.props.progressDisabled === true}
                        trackStyle={this.getStyleObjectFromProp(this.props.progressBarStyle) || styles.defaultTrackStyle}
                        handleStyle={this.getStyleObjectFromProp(this.props.progressHandleStyle) || styles.defaultHandleStyle}
                        railStyle={this.getStyleObjectFromProp(this.props.progressTrackStyle) || styles.defaultRailStyle}
                        style={this.getStyleObjectFromProp(this.props.progressBackgroundStyle) || styles.defaultBackgroundStyle}
                        onChange={this.props.onProgressChange}
                        onBeforeChange={this.props.onClickPause}
                        onAfterChange={this.props.onProgressComplete}
                    />
                </div>
                <h4 style={{ ...styles.defaultLabelStyle, ...this.getStyleObjectFromProp(this.props.progressLabelStyle) }}>{current.format(LABEL_FORMAT)}</h4>
            </div>
        );
    }

    protected getStyleObjectFromProp(styleProp: AudioViewStyleProp): React.CSSProperties {
        if (typeof styleProp === 'function') {
            return styleProp(this.props.useMode, this.props.recordingState, this.props.playbackState);
        }
        return styleProp || {};
    }
}

const styles: { [index: string]: React.CSSProperties } = {
    defaultStartButton: {
        height: '100px',
        width: '100px',
        border: `1px solid ${CONSTANTS.COLORS.BLUE}`,
        borderRadius: '50px'
    },
    defaultPauseButton: {
        height: '100px',
        width: '100px',
        border: `1px solid ${CONSTANTS.COLORS.BLUE}`,
        borderRadius: '50px'
    },
    defaultStopButton: {
        height: '80px',
        width: '80px',
        border: `1px solid ${CONSTANTS.COLORS.BLUE}`,
        borderRadius: '40px'
    },
    defaultTrackStyle: {
        backgroundColor: CONSTANTS.COLORS.DARK_BLUE_GREY
    },
    defaultHandleStyle: {
        backgroundColor: CONSTANTS.COLORS.BLUE
    },
    defaultRailStyle: {
        backgroundColor: CONSTANTS.COLORS.LIGHT_GREY
    },
    defaultBackgroundStyle: {
        backgroundColor: CONSTANTS.COLORS.TRANSPARENT
    },
    defaultProgressStyle: { display: CONSTANTS.FLEX, flex: 1 },
    submitStyle: { cursor: 'pointer', whiteSpace: 'nowrap', marginRight: 10, color: style.darkBlueColor },
    sliderContainer: { display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'center' },
    defaultLabelStyle: { marginRight: 20, fontSize: 16, width: 36 }
};



// WEBPACK FOOTER //
// ./src/_components/AudioRecorder/AbstractAudioView.tsx